import React, { useState } from 'react';
import { ImagesSection_MainQuery, Media, MediaRole, MediaType } from '../../../../codegen/graphql';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableMediaThumbnail } from './SortableMediaThumbnail';

interface SortableImageListProps {
  medias: NonNullable<ImagesSection_MainQuery['ad']>['medias'];
  isSelecting?: boolean;
  selectedRole?: MediaRole | null;
  selectedMedias?: NonNullable<ImagesSection_MainQuery['ad']>['medias'];
  onMediaClick: (media: Media) => void;
  onRemove: (mediaId: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

export function SortableImageList({
  medias,
  isSelecting = false,
  selectedRole,
  selectedMedias,
  onMediaClick,
  onRemove,
  onDragEnd,
}: SortableImageListProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Create custom mouse sensor with distance constraint
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  // Create custom touch sensor with delay and distance constraints
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 200,
      tolerance: 5,
    },
  });

  const sensors = useSensors(
    mouseSensor,
    touchSensor,
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragEndEvent) => {
    setIsDragging(true);
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    setActiveId(null);
    onDragEnd(event);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={medias.map(media => media.id)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {medias.map((media, index) => (
            <SortableMediaThumbnail
              key={media.id}
              media={media}
              index={index}
              onRemove={() => onRemove(media.id)}
              onMediaClick={onMediaClick}
              isDragging={media.id === activeId}
              isSelecting={isSelecting}
              selectedRole={selectedRole}
              selectedMedias={selectedMedias}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
} 